"use client";

import { useEffect } from "react";

/**
 * Scroll-effecten voor de eVilla-site:
 * 1. Scroll-gedreven onthulling ([data-reveal]) met stagger-delay via CSS var(--rd)
 * 2. Parallax op hero-afbeelding
 * 3. Actieve stap in het gesprek op de homepage
 * 4. Glass-header scroll-state (class .scrolled toevoegen)
 * 5. Respecteert prefers-reduced-motion
 */
export function ScrollEffects() {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* ---- 1. Onthulling ---- */
    const revealTargets = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]"),
    );

    if (reducedMotion) {
      revealTargets.forEach((el) => el.classList.add("in-view"));
    } else if (revealTargets.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add("in-view");
              observer.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.14, rootMargin: "0px 0px -5% 0px" },
      );
      revealTargets.forEach((el) => observer.observe(el));
      // Cleanup on unmount
      (window as unknown as Record<string, unknown>).__evillaRevealObserver = observer;
    }

    /* ---- 2. Hero parallax ---- */
    const hero = document.querySelector<HTMLElement>(".hx-hero");
    const heroImg = hero?.querySelector<HTMLElement>(".hx-hero-media img");

    if (hero && heroImg && !reducedMotion) {
      const onScroll = () => {
        const scrollY = window.scrollY;
        const heroHeight = hero.offsetHeight;
        if (scrollY <= heroHeight) {
          const progress = scrollY / heroHeight;
          const scale = 1 + progress * 0.06;
          const translateY = progress * 40;
          heroImg.style.transform = `scale(${scale}) translateY(-${translateY}px)`;
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      (window as unknown as Record<string, unknown>).__evillaHeroParallax = onScroll;

      // Markeer hero als in-view voor de scale-transition
      requestAnimationFrame(() => hero.classList.add("in-view"));
    } else if (hero) {
      hero.classList.add("in-view");
    }

    /* ---- 3. Actieve stap in het homepagegesprek ---- */
    const dialogueSteps = Array.from(
      document.querySelectorAll<HTMLElement>("[data-dialogue-step]"),
    );
    let onDialogueScroll: (() => void) | undefined;

    if (!reducedMotion && dialogueSteps.length > 0) {
      const updateDialogueStep = () => {
        const viewportMiddle = window.innerHeight / 2;
        const current = dialogueSteps.reduce((closest, step) => {
          const closestMiddle = closest.getBoundingClientRect().top + closest.offsetHeight / 2;
          const stepMiddle = step.getBoundingClientRect().top + step.offsetHeight / 2;
          return Math.abs(stepMiddle - viewportMiddle) < Math.abs(closestMiddle - viewportMiddle)
            ? step
            : closest;
        });
        dialogueSteps.forEach((step) => step.classList.toggle("is-current", step === current));
      };

      onDialogueScroll = updateDialogueStep;
      updateDialogueStep();
      window.addEventListener("scroll", onDialogueScroll, { passive: true });
      window.addEventListener("resize", onDialogueScroll);
    }

    /* ---- 4. Header scroll-state ---- */
    const header = document.querySelector<HTMLElement>(".site-header");
    if (header && !reducedMotion) {
      const onHeaderScroll = () => {
        header.classList.toggle("scrolled", window.scrollY > 20);
      };
      onHeaderScroll(); // init
      window.addEventListener("scroll", onHeaderScroll, { passive: true });
      (window as unknown as Record<string, unknown>).__evillaHeaderScroll = onHeaderScroll;
    }

    return () => {
      const obs = (window as unknown as Record<string, unknown>).__evillaRevealObserver as IntersectionObserver | undefined;
      obs?.disconnect();
      const hp = (window as unknown as Record<string, unknown>).__evillaHeroParallax as (() => void) | undefined;
      if (hp) window.removeEventListener("scroll", hp);
      if (onDialogueScroll) {
        window.removeEventListener("scroll", onDialogueScroll);
        window.removeEventListener("resize", onDialogueScroll);
      }
      const hs = (window as unknown as Record<string, unknown>).__evillaHeaderScroll as (() => void) | undefined;
      if (hs) window.removeEventListener("scroll", hs);
    };
  }, []);

  return null;
}